import apiClient from './apiClient';

class PromotionalService {
  async execute(method, resource, data) {
    return apiClient({
      method,
      url: resource,
      data,
    });
  }

  /**
   * Get all promotional content for homepage
   * @returns {Promise} All promotional content data
   */
  getAllPromotionalContent() {
    return this.execute("get", "/promotional-content/");
  }

  /**
   * Get ad videos
   * @param {Object} params - Query parameters (page, is_active, etc.)
   * @returns {Promise} Ad videos data
   */
  getAdVideos(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/ad-videos/?${queryString}` : "/ad-videos/";
    return this.execute("get", url);
  }

  /**
   * Get ad carousels
   * @param {Object} params - Query parameters (page, is_active, etc.)
   * @returns {Promise} Ad carousels data
   */
  getAdCarousels(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/ad-carousels/?${queryString}` : "/ad-carousels/";
    return this.execute("get", url);
  }

  /**
   * Get popular categories
   * @param {Object} params - Query parameters (page, category, etc.)
   * @returns {Promise} Popular categories data
   */
  getPopularCategories(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/popular-categories/?${queryString}` : "/popular-categories/";
    return this.execute("get", url);
  }

  /**
   * Get featured brands
   * @param {Object} params - Query parameters (page, manufacturer, etc.)
   * @returns {Promise} Featured brands data
   */
  getFeaturedBrands(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/featured-brands/?${queryString}` : "/featured-brands/";
    return this.execute("get", url);
  }

  /**
   * Get discount products
   * @param {Object} params - Query parameters (page, discount_percent, etc.)
   * @returns {Promise} Discount products data
   */
  getDiscountProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/discount-products/?${queryString}` : "/discount-products/";
    return this.execute("get", url);
  }

  /**
   * Get trending products
   * @param {Object} params - Query parameters (page, view_count, etc.)
   * @returns {Promise} Trending products data
   */
  getTrendingProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/trending-products/?${queryString}` : "/trending-products/";
    return this.execute("get", url);
  }

  /**
   * Increment view count for trending product
   * @param {number} productId - Product ID
   * @returns {Promise} Response data
   */
  incrementTrendingProductView(productId) {
    return this.execute("post", `/trending-products/${productId}/increment-view/`);
  }

  /**
   * Get new arrivals
   * @param {Object} params - Query parameters (page, arrival_date, etc.)
   * @returns {Promise} New arrivals data
   */
  getNewArrivals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/new-arrivals/?${queryString}` : "/new-arrivals/";
    return this.execute("get", url);
  }

  /**
   * Get explore beauty content
   * @param {Object} params - Query parameters (page, category_name, etc.)
   * @returns {Promise} Explore beauty data
   */
  getExploreBeauty(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/explore-beauty/?${queryString}` : "/explore-beauty/";
    return this.execute("get", url);
  }

  /**
   * Get celebration specials
   * @param {Object} params - Query parameters (page, celebration_type, etc.)
   * @returns {Promise} Celebration specials data
   */
  getCelebrationSpecials(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/celebration-specials/?${queryString}` : "/celebration-specials/";
    return this.execute("get", url);
  }

  /**
   * Get best sellers
   * @param {Object} params - Query parameters (page, sales_count, etc.)
   * @returns {Promise} Best sellers data
   */
  getBestSellers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/best-sellers/?${queryString}` : "/best-sellers/";
    return this.execute("get", url);
  }

  /**
   * Get health concerns
   * @param {Object} params - Query parameters (page, name, etc.)
   * @returns {Promise} Health concerns data
   */
  getHealthConcerns(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/health-concerns/?${queryString}` : "/health-concerns/";
    return this.execute("get", url);
  }

  /**
   * Get shop by health concerns
   * @param {Object} params - Query parameters (page, health_concern_name, etc.)
   * @returns {Promise} Shop by health concerns data
   */
  getShopByHealthConcerns(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/shop-by-health-concerns/?${queryString}` : "/shop-by-health-concerns/";
    return this.execute("get", url);
  }

  /**
   * Get memberships
   * @param {Object} params - Query parameters (page, membership_type, etc.)
   * @returns {Promise} Memberships data
   */
  getMemberships(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/memberships/?${queryString}` : "/memberships/";
    return this.execute("get", url);
  }

  /**
   * Purchase membership (requires authentication)
   * @param {number} membershipId - Membership ID
   * @returns {Promise} Purchase response data
   */
  purchaseMembership(membershipId) {
    return this.execute("post", "/user-memberships/purchase/", {
      membership_id: membershipId
    });
  }

  /**
   * Get individual ad video by ID
   * @param {number} id - Ad video ID
   * @returns {Promise} Ad video data
   */
  getAdVideoById(id) {
    return this.execute("get", `/ad-videos/${id}/`);
  }

  /**
   * Get individual ad carousel by ID
   * @param {number} id - Ad carousel ID
   * @returns {Promise} Ad carousel data
   */
  getAdCarouselById(id) {
    return this.execute("get", `/ad-carousels/${id}/`);
  }

  /**
   * Get individual trending product by ID
   * @param {number} id - Trending product ID
   * @returns {Promise} Trending product data
   */
  getTrendingProductById(id) {
    return this.execute("get", `/trending-products/${id}/`);
  }

  /**
   * Get individual discount product by ID
   * @param {number} id - Discount product ID
   * @returns {Promise} Discount product data
   */
  getDiscountProductById(id) {
    return this.execute("get", `/discount-products/${id}/`);
  }

  /**
   * Get individual best seller by ID
   * @param {number} id - Best seller ID
   * @returns {Promise} Best seller data
   */
  getBestSellerById(id) {
    return this.execute("get", `/best-sellers/${id}/`);
  }

  /**
   * Get individual new arrival by ID
   * @param {number} id - New arrival ID
   * @returns {Promise} New arrival data
   */
  getNewArrivalById(id) {
    return this.execute("get", `/new-arrivals/${id}/`);
  }
}

const promotionalService = new PromotionalService();
export default promotionalService;
